import type {
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import { encrypter, indexes, parseFetchData } from '@mab-common/utils'

import type { MsearchRequest } from '@elastic/elasticsearch/lib/api/types'

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import type { ArtworkCardProps } from '~/components/atoms/ArtworkCard/ArtworkCard'
import type { KeyMessageBannerProps } from '~/components/atoms/KeyMessageBanner/KeyMessageBanner'
import type { AdvantagesBannerProps } from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import type { AnimatedMessageBannerProps } from '~/components/blocks/AnimatedMessageBanner/AnimatedMessageBanner'
import type { ImageWithTextProps } from '~/components/blocks/ImageWithText/ImageWithText'
import type { TradingFloorItemProps } from '~/components/blocks/TradingFloorList/TradingFloorList'
import { useSettingsContext } from '~/contexts/settingsContext'
import {
  advantagesBanner,
  articleCard,
  artworkCard,
  createBaseSelector,
  detectBodyModuleTypes,
  getBodyModules,
  image,
  internationalizedArrayField,
  internationalizedField,
  internationalizedStringField,
  link,
  myPortfolioBanner,
  pageLanguageFilter,
  translatedLinkWithTranslation,
} from '~/schemas/common'
import {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { buildElasticSortFields } from '~/services/elastic/builder'
import useLatestTrading from '~/services/query/use-latest-trading'
import { useSanity } from '~/services/sanity'
import { buildTranslatedReferenceArray } from '~/utils/articles/translationHelpers'
import { addTranslations, type Locale } from '~/utils/i18n'
import { supportedLocales } from '~/utils/langPaths'
import { resolvePageSeoMeta } from '~/utils/seoHelper'
import { urlQuery } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'
import cryptoEncrypt = encrypter.cryptoEncrypt
import RecordTypes = indexes.RecordTypes
import EsIndexTypes = indexes.EsIndexTypes
import KeyMessageBanner, {
  ImageAlignmentVariants,
} from '~/components/atoms/KeyMessageBanner/KeyMessageBanner'
import VerticalPadder from '~/components/atoms/VerticalPadder/VerticalPadder'
import AdvantagesBanner from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import AnimatedMessageBanner from '~/components/blocks/AnimatedMessageBanner/AnimatedMessageBanner'
import ArtistProfileList from '~/components/blocks/ArtistProfileList/ArtistProfileList'
import GenreSlider from '~/components/blocks/GenreSlider/GenreSlider'
import ImageWithText from '~/components/blocks/ImageWithText/ImageWithText'
import LatestArtworks from '~/components/blocks/LatestArtworks/LatestArtworks'
import RelatedArticles from '~/components/blocks/RelatedArticles/RelatedArticles'
import SliderHero from '~/components/blocks/SliderHero/SliderHero'
import TradingFloorList from '~/components/blocks/TradingFloorList/TradingFloorList'
import { resolveBlock } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'
import { BannerTheme } from '~/enums/banner-theme'
import { PushInteractionLocation } from '~/utils/posthogEvents'

export interface PageData {
  modules: any
  banner: KeyMessageBannerProps
  overviewBanner: AnimatedMessageBannerProps
  pageSettings: {
    slides: any
    aboutDetails: ImageWithTextProps
    advantagesBanner: AdvantagesBannerProps
    homepageArticles: ArticlePreviewCardProps[]
    featuredArtworks: ArtworkCardProps[]
  }
  transparentMobileHeader: boolean
  preview: boolean
  query: any
  queryParams: any
  buildTicketData: TradingFloorItemProps[] | null
}

const getQuery = (
  limiter: any,
  scorer: any = '',
  lang: string,
  detectedTypes: string[]
) => {
  const homepageArticlesQuery = buildTranslatedReferenceArray(
    'homepageArticles',
    lang,
    articleCard
  )
  return `{
    "modules": *[_type == 'page' && slug.current == $slug && ${pageLanguageFilter(limiter)} ${limiter}] ${scorer}[0] {
      "body": body[] {
        ${getBodyModules(lang, detectedTypes)}
      }
    },
    "meta": *[_type == 'page' && slug.current == $slug && ${pageLanguageFilter(limiter)} ${limiter}] ${scorer}[0] { meta, _id },
    "banner": *[_type == 'banner' && showOnHomepage == true ${limiter}] ${scorer} | order(_createdAt desc) [0] {
        title,
        "subtitle": body,
        "images": {
          image
        },
        "textMask": applyOverlay,
        ${link}
    },
    "overviewBanner": *[_type == 'overviewBanner' && showOnHomepage == true ${limiter}] ${scorer} | order(_createdAt desc) [0] {
      title,
      slides,
      theme
    },
    "pageSettings": *[_type == 'settings'] [0] {
      "slides": homepageHeroSlides[] {
        ...,
        "tabTitle": ${internationalizedStringField('tabTitle')},
        "summary": ${internationalizedStringField('summary')},
        "title": ${internationalizedStringField('title')},
        ${image},
        ${translatedLinkWithTranslation},
      },
      ${homepageArticlesQuery},
      "featuredArtworks": homepageFeaturedArtworks[]->{
        ${artworkCard}
      },
      "aboutDetails": {
        "title": ${internationalizedStringField('homepageAbout.aboutTitle')},
        "body": ${internationalizedArrayField('homepageAbout.aboutBody', lang)},
        "image": homepageAbout.aboutImage,
        "link": {
          "text": ${internationalizedField('homepageAbout.linkText')},
          "externalLink": homepageAbout.linkExternal,
          "internalLink": homepageAbout.link->{
            ${urlQuery},
            },
          "terms": ctaTerms,
        },
      },
      ${advantagesBanner}
    },
    ${myPortfolioBanner}
    "notification": *[_type == 'notification' && showOnHomepage == true ${limiter}] ${scorer} | order(_createdAt desc) [0] {
        body,
        ${link},
        points,
        longText
    }
  }`
}

const latestArtworksEvents = {
  artwork: {
    method: SearchAnalystMethods.clickedObjectIDs,
    name: SearchAnalystEventNames.homeArtworkClicked,
  },
  artist: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.homeArtworkArtistClicked,
  },
}

const HomePage: NextPage<PageData> = (props: PageData) => {
  const { banner, pageSettings, overviewBanner, modules, buildTicketData } =
    props
  const blocks = modules?.body
  const router = useRouter()
  const { featuredArtists, genres, totalArtists } = useSettingsContext()
  const { data: tradingData } = useLatestTrading()
  const t = useTranslations('tradingFloorList')

  return (
    <>
      <SliderHero slides={pageSettings.slides} />
      <VerticalPadder>
        <ArtistProfileList
          title="Artists"
          artists={featuredArtists ? featuredArtists : []}
          totalArtists={totalArtists ? totalArtists : 0}
          allBtnHandler={() => router.push('/artists')}
          searchInsightsEvent={{
            method: SearchAnalystMethods.clickedFilters,
            name: SearchAnalystEventNames.homeArtistClicked,
          }}
          recordType={RecordTypes.ARTIST}
          showViewAll={true}
        />
      </VerticalPadder>

      <div className="offwhite-bg">
        <VerticalPadder>
          <LatestArtworks
            currated={pageSettings.featuredArtworks}
            searchInsightsEvents={latestArtworksEvents}
            offwhite
          />
        </VerticalPadder>
      </div>

      {(tradingData || buildTicketData) && (
        <VerticalPadder>
          <TradingFloorList
            items={tradingData ? tradingData : buildTicketData || []}
            title
            summary={t('summary')}
            teaser={'/artworks'}
            viewAllLink="/trading-floor"
          />
        </VerticalPadder>
      )}

      <AdvantagesBanner
        {...pageSettings.advantagesBanner}
        theme={BannerTheme.offwhite}
        analyticsInteractionLocation={
          PushInteractionLocation.HomePageValuationScreen
        }
        analyticsCtaSource="Homepage"
      />

      {banner?.images && (
        <KeyMessageBanner
          {...banner}
          imageAlign={ImageAlignmentVariants.full}
        />
      )}

      {genres && (
        <VerticalPadder>
          <GenreSlider
            genres={genres}
            searchInsightsEvent={{
              method: SearchAnalystMethods.clickedFilters,
              name: SearchAnalystEventNames.homeGenreClicked,
            }}
            title="Discover"
          />
        </VerticalPadder>
      )}

      {overviewBanner && <AnimatedMessageBanner {...overviewBanner} />}

      <ImageWithText
        {...pageSettings.aboutDetails}
        theme={ComponentTheme.secondary}
        flip={true}
      />

      {pageSettings.homepageArticles && (
        <RelatedArticles
          articles={pageSettings.homepageArticles}
          noPaddingBottom={true}
          searchInsightsEvent={{
            method: SearchAnalystMethods.clickedObjectIDs,
            name: SearchAnalystEventNames.homeArticleClicked,
          }}
        />
      )}

      {blocks && (
        <div>
          {blocks.map((block: any, i: number) => resolveBlock(block, i))}
        </div>
      )}
    </>
  )
}

const getStaticPropsOriginal = async function getStaticProps(
  context: GetStaticPropsContext,
  localizedData: { settings: any }
): Promise<GetStaticPropsResult<any>> {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { limiter, client, scorer } = await useSanity(context)
    // Extract language from context for internationalized queries
    const lang = (context?.params?.lang as Locale) || 'en'

    // STEP 1: Pre-query to detect which body module types are actually used
    const baseSelector = createBaseSelector(
      'page',
      [`slug.current == $slug`, pageLanguageFilter(limiter)],
      limiter,
      scorer
    )
    const detectedTypes = await detectBodyModuleTypes(
      client,
      baseSelector,
      { slug: '/', lang },
      ['body']
    )

    // STEP 2: Main query with only the required body modules
    const query = getQuery(limiter, scorer, lang, detectedTypes)

    const { meta: metaProps, ...data } = await client.fetch(query, {
      slug: '/',
      lang,
    })

    // Use centralized SEO helper with locale-aware templates
    await resolvePageSeoMeta(context, metaProps, 'page')

    const esQuery: MsearchRequest = {
      index: EsIndexTypes.TICKETS,
      searches: [
        {},
        {
          from: 0,
          size: 5,
          sort: buildElasticSortFields(RecordTypes.TICKETS),
        },
      ],
    }
    const esData = await fetch(`${process.env.SEARCH_API_BASE}/api/es`, {
      method: 'POST',
      body: cryptoEncrypt(
        JSON.stringify(esQuery),
        process.env.NEXT_PUBLIC_ELASTIC_SEARCH_MIDDLEWARE_TOKEN as string
      ),
    }).then(parseFetchData)

    const buildTicketData = ((esData?.[0] as any)?.hits?.hits || []).map(
      (h: any) => h._source
    )

    return {
      // revalidate: 21600, // Revalidates at most once every 6 hours (21600 seconds)
      props: {
        meta: metaProps.meta,
        notFound: Object.keys(data).length < 1,
        url: '/',
        ...data,
        buildTicketData: buildTicketData?.length ? buildTicketData : null,
        transparentMobileHeader: true,
        hasGoogleReviews: false,
        fullGoogleReviews: true,
        preview: !!context.preview,
        queryParams: { slug: '/' },
        jsonLd: {
          homeJsonLd: {
            baseUrl: 'https://www.myartbroker.com',
          },
        },
      },
    }
  } catch (e) {
    console.log('error:src/pages/[lang]/index:getStaticProps', e)
    return { props: { notFound: true } }
  }
}

// getStaticProps with addTranslations and withLocalizedSettings
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)

    if ('props' in result) {
      return {
        props: await addTranslations(result.props, context),
      }
    }

    return result
  }
)

// Custom getStaticPaths for homepage folder structure
export const getStaticPaths = async () => {
  const paths = supportedLocales.map(lang => ({ params: { lang } }))
  return {
    paths,
    fallback: 'blocking' as const,
  }
}

export default HomePage
